import React, { useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Typography,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/Index";
import { tokens } from "../../../../theme";
import CustomTextField from "../../../../components/common/Textfield";

import { BookingData } from "../../../../types/bookings";
import { Venue } from "../../../../types/venue";
import { useVenueSearch } from "../hooks/UseBookingsSearch";
import AddVenueDialog from "./AddVenueDialog";
import CustomSelect from "../../../../components/common/Select";

interface VenueSearchFieldProps {
  bookingData: BookingData;
  setBookingData: React.Dispatch<React.SetStateAction<BookingData>>;
}

const VenueSearchField: React.FC<VenueSearchFieldProps> = ({
  bookingData,
  setBookingData,
}) => {
  const theme = useTheme();
  const themeState = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, themeState);
  const color400 = colors.primary[400] || "#724E41";

  const [dialogOpen, setDialogOpen] = useState(false);

  const {
    venueOptions,
    venueLoading,
    selectedVenue,
    setSelectedVenue,
    setVenueSearchTerm,
  } = useVenueSearch(bookingData.venue_ids?.[0]);

  const applyVenue = (value: Venue | null) => {
    setSelectedVenue(value);
    setBookingData((prev) => ({
      ...prev,
      venue_ids: value ? [String(value.id)] : [],
      venue_name: value?.name || "",
      venue_parent_name: null,
      venue_room_id: null,
      address_one: value?.address_one || "",
      address_two: value?.address_two || "",
      suburb: value?.suburb || "",
      city: value?.city || "",
      areAddressFieldsDisabled: !!value,
    }));
  };

  const applyRoom = (roomId: number) => {
    const room = selectedVenue?.children?.find((r) => r.id === roomId);
    if (!room) return;
    setBookingData((prev) => ({
      ...prev,
      venue_room_id: room.id,
      venue_parent_name: selectedVenue?.name || null,
    }));
  };

  const handleVenueSaved = (venue: Venue) => applyVenue(venue);

  const hasRooms = (selectedVenue?.children?.length ?? 0) > 0;

  return (
    <Box>
      <Autocomplete
        fullWidth
        options={venueOptions}
        getOptionLabel={(o: Venue) => o.name || ""}
        value={selectedVenue}
        onChange={(_e, value) => applyVenue(value)}
        onInputChange={(_e, value) => setVenueSearchTerm(value)}
        loading={venueLoading}
        filterOptions={(x) => x}
        renderInput={(params) => (
          <CustomTextField
            {...params}
            label="Venue"
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {venueLoading && (
                    <CircularProgress size={18} sx={{ color: color400 }} />
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      <Typography
        variant="caption"
        sx={{ color: "#A0AEC0", display: "block", mt: 0.5, fontSize: "11px" }}
      >
        Type at least 3 characters to search venues
      </Typography>

      {hasRooms && (
        <Box sx={{ mt: 2 }}>
          <CustomSelect
            label="Room (optional)"
            value={bookingData.venue_room_id ?? ""}
            onChange={(e) => {
              const val = Number(e.target.value);
              if (!val) {
                setBookingData((prev) => ({
                  ...prev,
                  venue_room_id: null,
                  venue_parent_name: null,
                }));
              } else {
                applyRoom(val);
              }
            }}
          >
            <MenuItem value="">
              <em>No specific room</em>
            </MenuItem>
            {selectedVenue!.children!.map((room) => (
              <MenuItem key={room.id} value={room.id}>
                {room.name}
                {room.capacity ? ` — ${room.capacity} capacity` : ""}
              </MenuItem>
            ))}
          </CustomSelect>
        </Box>
      )}

      <Button
        startIcon={<AddIcon />}
        onClick={() => setDialogOpen(true)}
        sx={{
          color: color400,
          mt: 1,
          p: 0,
          minWidth: 0,
          textTransform: "none",
          fontWeight: 600,
          fontSize: "12px",
          "&:hover": { backgroundColor: "transparent", opacity: 0.8 },
        }}
      >
        Add Venue
      </Button>

      <AddVenueDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={handleVenueSaved}
      />
    </Box>
  );
};

export default VenueSearchField;
